import React, {useState, useEffect, useCallback, useContext, useMemo} from 'react'
import {Tabs, Tab} from 'react-bootstrap'
import {
  Grid,
  Box,
  Button,
  Modal,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@material-ui/core'
import './InfoArea.scss'
import CheckIcon from '@material-ui/icons/Check'
import ClearIcon from '@material-ui/icons/Clear'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import ReservationReserve from '../ReservationReserve/ReservationReserve'
import _ from 'lodash'
import moment from 'moment'
import NextStepActionModal from './NextStepActionModal'
import usePutApproveRejectReducer from '../../state/reservation/put-approve-reject/reducer'
import {putApproveReject} from '../../state/reservation/put-approve-reject/actions'
import {UserContext} from '../../context/User'
import {ReservationContext} from '../../context/Reservation'
import LoadingIndicator from '../ReservationConfiguration/LoadingIcon'
import {ToastMessageContext} from '../../context/ToastMessage/ToastMessage'
import {ReservationConfigurationContext} from '../../context/ReservationConfiguration'
import PendingDesign from './PendingInfoDesign'
import UpcomingDesign from './UpcomingInfoDesign'
import OngoingDesign from './OngoingInfoDesign'
import PreviousDesign from './PreviousInfoDesign'
import {deleteReservation} from '../../state/reservation/delete-reservation/action'
import useDeleteCalendarViewReducer from '../../state/reservation/delete-reservation/reducer'
import useCalendarViewContext from '../../context/CalendarView'
import {SettingsContext} from '../../context/Settings/Settings'
import {useModal} from '../../common/Modal'
import {useTranslation} from 'react-i18next'
import {transformDateFormat, transformTimeFormat} from '../../context/Reservation/util'

const InfoArea = ({
  itemSelected,
  status,
  state,
  tabActive,
  setItemSelected,
  setItemUpdated,
  approvalManagerGroups,
}) => {
  const {t} = useTranslation()
  const simpleModal = useModal()
  const {groups} = useCalendarViewContext()
  const settings = useContext(SettingsContext)
  const [otherAreaTabActive, setOtherAreaTabActive] = useState('details')
  const [openNextStepAction, setOpenNextStepAction] = useState(false)
  const [popReserve, setPopReserve] = useState(false)
  const [remove, deleteDispatch] = useDeleteCalendarViewReducer()
  const [rejectClick, setRejectClick] = useState(false)
  const [approveClick, setApproveClick] = useState(false)
  const [reasonValue, setReasonValue] = useState('')
  const [submitResults, dispatch] = usePutApproveRejectReducer()
  const currentUser = useContext(UserContext)
  const ctxToast = useContext(ToastMessageContext)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [selectedAction, setSelectedAction] = useState(null)
  const [isReturn, setIsReturn] = useState(false)
  const reservationDone = moment(itemSelected.end_date).diff(moment(), 'hours')

  const {reservation, clearReservation, clearReservationsData, update} = useContext(
    ReservationContext
  )

  const date_format = transformDateFormat(currentUser?.details?.account_date_format)
  const time_format = transformTimeFormat(currentUser?.details?.account_time_format)
  const canDelete = useMemo(() => {
    if (!itemSelected && groups.length === 0 && !settings?.data) return false
    const _group = groups.data.find(f => f.id === itemSelected?.entity_id)
    if (!_group) return false

    return (
      !!settings.data?.user_access_rules?.can_delete?.reservations.find(f => f === _group.key) ||
      false
    )
  }, [groups, itemSelected, settings])

  const canEdit = useMemo(() => {
    if (!itemSelected && groups.length === 0 && !settings?.data) return false
    const _group = groups.data.find(f => f.id === itemSelected?.entity_id)
    if (!_group) return false

    return (
      !!settings.data?.user_access_rules?.can_edit?.reservations.find(f => f === _group.key) ||
      false
    )
  }, [groups, itemSelected, settings])

  const {groupActions, getGroupActions, getConfiguration, gconfiguration} = React.useContext(
    ReservationConfigurationContext
  )

  const canAction = gconfiguration?.data?.active || false

  const reservationStarted = moment(itemSelected.start_date).diff(moment(), 'minutes')

  function convertDateTime(date) {
    return moment(new Date(date)).format(`${date_format} ${time_format}`)
  }

  function otherAreaTabSelect(key) {
    setOtherAreaTabActive(key)
  }

  const handleCloseModal = () => {
    setOpenNextStepAction(false)
    setMenuAnchor(null)
  }

  const isEditable = useMemo(() => {
    if (tabActive === 'previous') return false
    return true
  }, [tabActive])

  const getApprovalManager = () => {
    let op = false
    approvalManagerGroups.forEach(group => {
      if (group === itemSelected.entity_id) {
        op = true
      }
    })
    return op
  }

  const handleActionClick = (e, action) => {
    setOpenNextStepAction(true)
    setSelectedAction(action)
    if (tabActive === 'ongoing') {
      setIsReturn(true)
    } else {
      setIsReturn(false)
    }
  }

  const handleSaveNextStepAction = () => {}
  const clickReject = event => {
    setRejectClick(true)
  }

  const handleOpenActionsMenu = e => {
    setMenuAnchor(e.target)
  }

  const handleCloseActionsMenu = () => {
    setMenuAnchor(null)
  }

  const clickApprove = event => {
    setApproveClick(true)
  }

  const putCallApi = useCallback(
    params => {
      putApproveReject(dispatch, params)
    },
    [dispatch]
  )

  const statusDesignShow = () => {
    if (tabActive === 'pending') {
      return (
        <PendingDesign
          state={state}
          status={status}
          rejectClick={rejectClick}
          approveClick={approveClick}
          reservationDone={reservationDone}
        />
      )
    } else if (tabActive === 'upcoming') {
      return (
        <UpcomingDesign
          state={state}
          status={status}
          hasApprovalManager={getApprovalManager()}
          rejectClick={rejectClick}
          approveClick={approveClick}
          reservationDone={reservationDone}
          reservationStarted={reservationStarted}
        />
      )
    } else if (tabActive === 'ongoing') {
      return (
        <OngoingDesign
          state={state}
          status={status}
          hasApprovalManager={getApprovalManager()}
          rejectClick={rejectClick}
          approveClick={approveClick}
          reservationDone={reservationDone}
          reservationStarted={reservationStarted}
        />
      )
    } else {
      return (
        <PreviousDesign
          state={state}
          status={status}
          hasApprovalManager={getApprovalManager()}
          rejectClick={rejectClick}
          approveClick={approveClick}
          reservationDone={reservationDone}
        />
      )
    }
  }

  const handleSubmit = event => {
    const params = {
      manager_id: currentUser.details.id,
      entity_object_ids: itemSelected.entity_object_ids,
      action_object_ids: itemSelected.action_object_ids,
      status_action: approveClick ? 'approved' : 'rejected',
      comment: reasonValue,
    }
    putCallApi(params)
  }

  useEffect(() => {
    if (!_.isEmpty(submitResults.data)) {
      if (submitResults?.data?.errors) {
        ctxToast.popMessage('Error encountered update failed.', 'error', null)
      } else {
        setItemUpdated(itemSelected)
        ctxToast.popMessage('Reservation Item updated successfully.', 'success', null)
      }
      setItemSelected({})
    }
    // eslint-disable-next-line
  }, [submitResults])

  useEffect(() => {
    if (
      (tabActive === 'upcoming' || tabActive === 'ongoing' || tabActive === 'previous') &&
      gconfiguration.data.action_steps &&
      itemSelected.entity_id &&
      !gconfiguration.isLoading
    ) {
      let actionsIds = gconfiguration.data.action_steps.map(s => {
        return s.action_id
      })
      //call action objects API
      getGroupActions({group_id: itemSelected.entity_id, params: {action_ids: actionsIds}})
    }
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, gconfiguration, tabActive, itemSelected.entity_id])

  useEffect(() => {
    //call configuration API
    if (itemSelected.entity_id) {
      getConfiguration({group_id: itemSelected.entity_id})
    }
    return () => {}
    // eslint-disable-next-line
  }, [itemSelected.entity_id])

  useEffect(() => {
    setOtherAreaTabActive('details')
    setRejectClick(false)
    setApproveClick(false)
  }, [itemSelected])

  const handleReasonValue = event => {
    setReasonValue(event.target.value)
  }

  const handleDelete = event => {
    const temp_data = {
      group_object_ids: itemSelected.entity_object_ids,
      action_object_ids: itemSelected.action_object_ids,
    }
    const reserved_for = itemSelected.reserve_for.value
    const from = moment(itemSelected.start_date).format('MM/DD/YYYY h:mm A')
    const to = moment(itemSelected.end_date).format('MM/DD/YYYY h:mm A')
    if (simpleModal) {
      simpleModal.show({
        scheme: 'destructive',
        title: t('reservations.confirmations.delete.title', {
          reservation_uid: `#${itemSelected.uid}`,
        }),
        description: t('reservations.confirmations.delete.description', {
          reserved_for: reserved_for,
          reservation_start: from,
          reservation_end: to,
        }),
        buttons: {
          affirmative: {
            label: t('reservations.confirmations.delete.affirmative'),
            click: (data, modal) => {
              deleteReservation(deleteDispatch, temp_data).then(res => {
                setItemUpdated(itemSelected)
                setItemSelected({})
                ctxToast.popMessage(res.message, 'success', null)
              })
              modal.hide()
            },
          },
          negative: {
            label: t('reservations.confirmations.delete.negative'),
          },
        },
      })
    }
  }

  const handleEdit = e => {
    if (currentUser.details.reservation_enabled) {
      setPopReserve(true)
      update('fromGantt', false)
      update('isSelection', false)
      update('is_edit', true)
      update('date_from', moment(itemSelected.start_date))
      update('date_to', moment(itemSelected.end_date))
      update(
        'selected_items',
        itemSelected.entity_object_ids.map((i, o) => {
          return {
            id: i,
            name: `${itemSelected.items[o].primary ? itemSelected.items[o].primary : '-'}
            | ${itemSelected.items[o].secondary ? itemSelected.items[o].secondary : '-'}`,
          }
        })
      )
      update(
        'original_items',
        itemSelected.entity_object_ids.map((i, o) => {
          return {id: i}
        })
      )
      update('reserve_for_field', {})
      update('comments', itemSelected.comments)
      update('selected_group', {id: itemSelected.entity_id})
      update('reserved_for', {
        name: itemSelected.reserve_for.value,
        id: itemSelected.reserve_for.value_id,
        field_id: itemSelected.reserve_for.field_id,
      })
      update('action_ids', itemSelected.reserve_for.action_ids)
      update('additional_fields', itemSelected.additional_fields)
      update('selected_field', {})
      update('selected_group_id', itemSelected.entity_id)
      update('reservation_notification', itemSelected.reservation_notification || [])
      update('reservation_uid', itemSelected.uid)
      update('deleted_item_ids', [])
      update('status', itemSelected.status)
      update('states', itemSelected.states)
    }
  }

  const handleClose = e => {
    setPopReserve(false)
    clearReservation()
    clearReservationsData()
  }

  const handleReserve = id => {
    setPopReserve(false)
    clearReservation()
  }

  const getReturnAction = () => {
    let op = {}
    if (groupActions.data.length) {
      groupActions.data.forEach(ga => {
        if (ga.id === itemSelected.performed_actions[0].action_id) {
          if (ga.change_return_name) {
            op = {
              name: ga.return_name,
              action: ga,
            }
          } else {
            op = {
              name: `Return ${ga.name}`,
              action: ga,
            }
          }
        }
      })
    }
    return op
  }

  const getActions = () => {
    let op = []
    if (groupActions.data.length) {
      groupActions.data.forEach(ga => {
        if (itemSelected.reserve_for.action_ids?.includes(ga.id.toString())) {
          let allowed = false
          itemSelected.permitted_action_ids.forEach(pa => {
            if (pa.action_id === ga.id && pa.can_perform) {
              allowed = true
            }
          })
          if (allowed) {
            op.push(ga)
          }
        }
      })
    }
    return op
  }

  const getActionName = () => {
    let op = {}
    if (groupActions.data.length) {
      groupActions.data.forEach(ga => {
        if (itemSelected.performed_actions[0]?.action_id === ga.id) {
          op = ga
        }
      })
    }
    return op
  }

  const getActionReturnName = ga => {
    let op = {}
    if (ga.change_return_name) {
      op = {
        name: ga.return_name,
        action: ga,
      }
    } else {
      op = {
        name: `Return ${ga.name}`,
        action: ga,
      }
    }
    return op
  }

  return (
    <>
      {_.isEmpty(itemSelected) && (
        <div className="reservation-bookings-info-area__not-selected">
          {' '}
          Select a Reservation to view more information!{' '}
        </div>
      )}
      {!_.isEmpty(itemSelected) && (
        <div className="reservation-bookings-info-area__other-area-main">
          <div className="reservation-bookings-info-area__other-area-title">
            <div style={{display: '-webkit-inline-box', marginRight: '10px'}}>
              <Box color="#5c5d5e">Reservation –&nbsp;</Box>
              {`#${itemSelected.uid}`}
            </div>
            {canDelete && (
              <IconButton
                onClick={e => handleDelete(e)}
                aria-label="delete"
                style={{color: '#596175', float: 'right', marginTop: '5px'}}
              >
                <DeleteIcon />
              </IconButton>
            )}
            {isEditable && canEdit && (
              <IconButton
                onClick={e => handleEdit(e)}
                aria-label="edit"
                style={{color: '#596175', float: 'right', marginTop: '5px'}}
              >
                <EditIcon />
              </IconButton>
            )}

            <Modal
              style={{position: 'absolute'}}
              open={popReserve}
              onClose={handleClose}
              aria-labelledby="simple-modal-title"
              aria-describedby="simple-modal-description"
            >
              <ReservationReserve
                fromGantt={reservation.fromGantt}
                onClose={handleClose}
                handleReserve={handleReserve}
              />
            </Modal>
          </div>
          <Grid item container xs={12} style={{marginTop: '20px'}}>
            <Grid item xs={6}>
              <div style={{fontSize: '16px'}}>Status</div>
              {statusDesignShow()}
            </Grid>
            <Grid
              item
              xs={6}
              style={{margin: '40px auto', display: 'flex', alignItems: 'baseline', gap: '5px'}}
            >
              <Grid container>
                <Grid item xs={6} hidden={rejectClick || approveClick}>
                  {canAction &&
                    (tabActive === 'pending' ||
                      (tabActive === 'previous' &&
                        getApprovalManager() &&
                        state === 'rejected')) && (
                      <Button
                        onClick={e => clickApprove(e)}
                        variant="outlined"
                        className="reservation-bookings-info-area__btn-styles"
                      >
                        <div>
                          <CheckIcon />
                        </div>
                        <div>Approve</div>
                      </Button>
                    )}
                  {canAction &&
                    (tabActive === 'pending' ||
                      (tabActive === 'upcoming' && getApprovalManager())) && (
                      <Button
                        variant="outlined"
                        onClick={e => clickReject(e)}
                        className="reservation-bookings-info-area__btn-styles reservation-bookings-info-area__btn-reject"
                      >
                        <div>
                          <ClearIcon />
                        </div>
                        Reject
                      </Button>
                    )}
                </Grid>
                {groupActions.isLoading && (tabActive === 'upcoming' || tabActive === 'ongoing') ? (
                  <CircularProgress />
                ) : (
                  <Grid item xs={6}>
                    {canAction &&
                    itemSelected.can_perform_next_action &&
                    tabActive === 'upcoming' &&
                    getActions().length ? (
                      getActions().length === 1 ? (
                        <Button
                          variant="outlined"
                          onClick={e => handleActionClick(e, getActions()[0])}
                          className="ap-button--primary ap-ellipsis"
                          style={{maxWidth: 155, marginTop: 20}}
                        >
                          <div className="ap-ellipsis" title={getActions()[0].name}>
                            {getActions()[0].name}
                          </div>
                        </Button>
                      ) : canAction && tabActive === 'upcoming' ? (
                        <>
                          <Button
                            className="ap-button--primary"
                            style={{marginLeft: 5, marginTop: 20}}
                            aria-controls="simple-menu"
                            aria-haspopup="true"
                            onClick={handleOpenActionsMenu}
                          >
                            Actions
                          </Button>
                          <Menu
                            id="simple-menu"
                            anchorEl={menuAnchor}
                            keepMounted
                            open={Boolean(menuAnchor)}
                            onClose={handleCloseActionsMenu}
                            etContentAnchorEl={null}
                            anchorOrigin={{
                              horizontal: 'left',
                              vertical: 'top',
                            }}
                            transformOrigin={{
                              horizontal: 'left',
                              vertical: 'top',
                            }}
                          >
                            {getActions().length &&
                              getActions().map(action => {
                                return (
                                  <MenuItem
                                    onClick={e => handleActionClick(e, action)}
                                    className="ap-ellipsis"
                                  >
                                    {action.name}
                                  </MenuItem>
                                )
                              })}
                          </Menu>
                        </>
                      ) : null
                    ) : null}
                    {canAction &&
                    tabActive === 'ongoing' &&
                    groupActions.data.length &&
                    getReturnAction().name ? (
                      <Button
                        variant="outlined"
                        onClick={e => handleActionClick(e, getReturnAction().action)}
                        className="ap-button--primary"
                        style={{maxWidth: 155}}
                      >
                        <div className="ap-ellipsis" title={getReturnAction().name}>
                          {getReturnAction().name}
                        </div>
                      </Button>
                    ) : null}
                  </Grid>
                )}
              </Grid>
              <div hidden={!rejectClick && !approveClick}>
                <TextField
                  id="reasonField"
                  label="Reason"
                  variant="outlined"
                  size="small"
                  onChange={handleReasonValue}
                  style={{marginBottom: '10px', width: '100%'}}
                />
                {!submitResults.isLoading && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={e => handleSubmit(e)}
                    style={{
                      color: '#303a52',
                      backgroundColor: '#eaebee',
                      textTransform: 'none',
                    }}
                  >
                    Submit
                  </Button>
                )}
                {submitResults.isLoading && <LoadingIndicator />}
              </div>
            </Grid>
          </Grid>
          <div>
            <Tabs
              defaultActiveKey="details"
              activeKey={otherAreaTabActive}
              onSelect={e => otherAreaTabSelect(e)}
              variant="pills"
              className={`reservation-bookings-info-area__other-area-tab`}
              style={{flexWrap: 'nowrap'}}
            >
              <Tab eventKey="details" title="Details">
                <div style={{marginTop: '20px', marginLeft: '20px', fontSize: '16px'}}>
                  <Grid item container xs={12}>
                    <Grid item xs={5}>
                      <div style={{color: '#596175'}}>From</div>
                      <div>{convertDateTime(itemSelected.start_date)}</div>
                    </Grid>
                    <Grid item xs={5}>
                      <div style={{color: '#596175'}}>To</div>
                      <div>{convertDateTime(itemSelected.end_date)}</div>
                    </Grid>
                  </Grid>
                  <div style={{color: '#596175', marginTop: '15px'}}>For</div>
                  <div>{itemSelected.reserve_for.value}</div>
                  <div style={{color: '#596175', marginTop: '15px'}}>Items</div>
                  <div style={{height: '150px', overflow: 'auto'}}>
                    {itemSelected.items.map((data, index) => {
                      return (
                        <div style={{marginTop: '5px'}} key={`item-${index}`}>
                          {`${data.primary} - ${data.secondary}`}
                        </div>
                      )
                    })}
                  </div>
                  <div>
                    <div style={{color: '#596175', marginTop: '15px'}}>Comments</div>
                    <div>{itemSelected.comments || '-'}</div>
                  </div>
                </div>
              </Tab>
              <Tab
                eventKey="additional-fields"
                title="Additional Fields"
                style={{height: '461px', overflowY: 'auto'}}
              >
                {_.isEmpty(itemSelected.additional_fields) && (
                  <div> Current Reservation has no additional fields. </div>
                )}
                {!_.isEmpty(itemSelected.additional_fields) && (
                  <Grid
                    item
                    container
                    xs={12}
                    style={{marginTop: '10px', marginLeft: '20px', fontSize: '16px'}}
                  >
                    {itemSelected.additional_fields.map((data, index) => {
                      return (
                        <Grid key={`additional-fields-${index}`} item xs={6}>
                          <div style={{color: '#596175', marginTop: '15px'}}>{data.field_name}</div>
                          <div style={{wordBreak: 'break-word', marginRight: '10px'}}>
                            {data.value || '-'}
                          </div>
                        </Grid>
                      )
                    })}
                  </Grid>
                )}
              </Tab>
              <Tab
                eventKey="action-fields"
                title={tabActive === 'pending' || tabActive === 'upcoming' ? '' : 'Action Fields'}
                disabled={
                  tabActive === 'pending' ||
                  tabActive === 'upcoming' ||
                  itemSelected.performed_actions.length === 0
                }
              >
                <Grid container style={{marginTop: '10px', marginLeft: '20px', fontSize: '16px'}}>
                  <Grid item xs={12}>
                    {getActionName().name}
                  </Grid>
                  {itemSelected.next_action_fields[0]?.next_actions[0]?.values.map(val => {
                    if (!val.is_return_field) {
                      return (
                        <Grid item xs={6}>
                          <div
                            style={{color: '#596175', marginTop: '15px'}}
                            className="ap-m-0 ap-typography ap-typography--gray"
                          >
                            {val.field_name}
                          </div>
                          {!_.isNil(val.value) && (
                            <div
                              style={{wordBreak: 'break-word', marginRight: '10px'}}
                              className="ap-m-0 ap-typography "
                            >
                              {val.value}
                            </div>
                          )}
                          {_.isNil(val.value) && (
                            <div className="ap-m-0 ap-typography ap-typography--sm ap-typography--gray">
                              n/a
                            </div>
                          )}
                        </Grid>
                      )
                    }
                  })}
                  <Grid item xs={12} style={{marginTop: '15px'}}>
                    {getActionReturnName(getActionName()).name}
                  </Grid>
                  {itemSelected.next_action_fields[0]?.next_actions[0]?.values.map(val => {
                    if (val.is_return_field) {
                      return (
                        <Grid item xs={6}>
                          <div
                            style={{color: '#596175', marginTop: '15px'}}
                            className="ap-m-0 ap-typography ap-typography--gray"
                          >
                            {val.field_name}
                          </div>
                          {!_.isNil(val.value) && (
                            <div
                              style={{wordBreak: 'break-word', marginRight: '10px'}}
                              className="ap-m-0 ap-typography "
                            >
                              {val.value}
                            </div>
                          )}
                          {_.isNil(val.value) && (
                            <div className="ap-m-0 ap-typography ap-typography--sm ap-typography--gray">
                              n/a
                            </div>
                          )}
                        </Grid>
                      )
                    }
                  })}
                </Grid>
              </Tab>
              <Tab eventKey="empty" title="" disabled />
            </Tabs>
          </div>
        </div>
      )}

      <NextStepActionModal
        open={openNextStepAction}
        onClose={handleCloseModal}
        tabActive={tabActive}
        onSave={handleSaveNextStepAction}
        action={selectedAction}
        group_id={itemSelected.entity_id}
        uid={itemSelected.uid}
        object_ids={itemSelected.entity_object_ids}
        isReturn={isReturn}
        setItemSelected={setItemSelected}
        setItemUpdated={setItemUpdated}
        itemSelected={itemSelected}
      />
    </>
  )
}

export default InfoArea
